import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FiCheckSquare, FiArrowRight, FiAlertCircle, FiCalendar, FiTag,
  FiPaperclip, FiMessageSquare, FiClock, FiFlag, FiList, FiCheckCircle
} from 'react-icons/fi';
import PublicPageHeader from '../../../components/common/PublicPageHeader';
import PublicPageFooter from '../../../components/common/PublicPageFooter';
import DocsSidebar from '../../../components/docs/DocsSidebar';

const TaskManagement = () => {
  const priorities = [
    { level: 'Critical', color: 'bg-red-500', description: 'Urgent tasks that need immediate attention' },
    { level: 'High', color: 'bg-orange-500', description: 'Important tasks with upcoming deadlines' },
    { level: 'Medium', color: 'bg-yellow-500', description: 'Standard tasks with normal priority' },
    { level: 'Low', color: 'bg-green-500', description: 'Tasks that can be done when time permits' }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <PublicPageHeader />
      
      <div className="flex">
        <DocsSidebar currentPath="/docs/personal/tasks" />
        
        <main className="flex-1 max-w-4xl mx-auto px-6 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-8">
            <Link to="/docs" className="hover:text-blue-600">Docs</Link>
            <span>/</span>
            <Link to="/docs/personal" className="hover:text-blue-600">Personal Workspace</Link>
            <span>/</span>
            <span className="text-gray-900 dark:text-white">Task Management</span>
          </nav>

          {/* Header */}
          <div className="mb-12">
            <div className="inline-flex p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl mb-4">
              <FiCheckSquare className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Task Management</h1>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Learn how to create, organize, and track tasks effectively in your personal workspace.
            </p>
          </div>

          {/* Creating Tasks */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Creating Tasks</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Tasks are the building blocks of your productivity. Here's how to create effective tasks:
            </p>
            
            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6 mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Quick Task Creation</h3>
              <ol className="space-y-3">
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  <span className="text-gray-700 dark:text-gray-200">Click the <strong>"+ Add Task"</strong> button in any project or view</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  <span className="text-gray-700 dark:text-gray-200">Enter a descriptive task title</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  <span className="text-gray-700 dark:text-gray-200">Press Enter or click Create to save</span>
                </li>
              </ol>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <FiAlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 dark:text-blue-300">Pro Tip</h4>
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    Use keyboard shortcut <code className="bg-blue-100 dark:bg-blue-900/30 px-1 rounded">Ctrl/Cmd + N</code> to quickly create a new task from anywhere.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Task Properties */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Task Properties</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Each task can have multiple properties to help you organize and track your work:
            </p>

            <div className="space-y-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiFlag className="w-5 h-5 text-red-500" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Priority</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">Set task importance to focus on what matters most.</p>
                <div className="flex flex-wrap gap-2">
                  {priorities.map((priority, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm">
                      <div className={`w-3 h-3 rounded-full ${priority.color}`}></div>
                      <span className="text-gray-700 dark:text-gray-200">{priority.level}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiCalendar className="w-5 h-5 text-blue-500" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Due Date</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Set deadlines to stay on track. Tasks will appear in your calendar view 
                  and you'll receive reminders as the due date approaches.
                </p>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiTag className="w-5 h-5 text-purple-500" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Labels</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Add custom labels to categorize tasks. Use labels like "Bug", "Feature", 
                  "Research", etc. to filter and group related tasks.
                </p>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiPaperclip className="w-5 h-5 text-green-500" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Attachments</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Attach files, images, and documents directly to tasks. Keep all relevant 
                  materials together for easy access.
                </p>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-2">
                  <FiMessageSquare className="w-5 h-5 text-orange-500" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">Comments</h3>
                </div>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Add notes, discuss with team members, and track decisions. All comments 
                  are timestamped and attributed.
                </p>
              </div>
            </div>
          </section>

          {/* Subtasks */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Subtasks & Checklists</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Break down complex tasks into smaller, manageable pieces with subtasks:
            </p>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Working with Subtasks</h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <FiList className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">Create Subtasks</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Click "Add subtask" within any task to create child tasks.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <FiCheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">Track Progress</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Parent task shows completion percentage based on subtask status.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <FiClock className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">Individual Due Dates</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">Each subtask can have its own due date and assignee.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Task Statuses */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Task Statuses</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Track task progress through different statuses:
            </p>

            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                <span className="text-gray-700 dark:text-gray-200">To Do</span>
              </div>
              <div className="flex items-center space-x-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className="text-blue-700">In Progress</span>
              </div>
              <div className="flex items-center space-x-2 px-4 py-2 bg-yellow-100 rounded-lg">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span className="text-yellow-700">In Review</span>
              </div>
              <div className="flex items-center space-x-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-green-700">Done</span>
              </div>
            </div>
          </section>

          {/* Navigation */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Related Guides</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <Link
                to="/docs/personal/projects"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 border border-transparent hover:border-blue-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Project Management</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
              <Link
                to="/docs/features/views"
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-xl hover:bg-blue-50 dark:bg-blue-900/30 border border-transparent hover:border-blue-200 transition-all"
              >
                <span className="font-medium text-gray-900 dark:text-white">Views & Boards</span>
                <FiArrowRight className="text-gray-400 dark:text-gray-500" />
              </Link>
            </div>
          </section>

          {/* Article Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Last updated: January 27, 2026
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500 dark:text-gray-400">Was this helpful?</span>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  Yes
                </button>
                <button className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  No
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      <PublicPageFooter />
    </div>
  );
};

export default TaskManagement;
